<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="regen-modal-root"
      data-dismissable-layer=""
      @pointerdown.stop
      @mousedown.stop
      @click.stop
    >
      <!-- Backdrop with blur -->
      <Transition name="regen-fade" appear>
        <div class="regen-backdrop" @click="isOpen = false" />
      </Transition>

      <!-- Modal Shell -->
      <Transition name="regen-pop" appear>
        <div
          class="regen-shell"
          role="dialog"
          aria-modal="true"
          :aria-label="'Regenerate ' + sectionLabel"
          @pointerdown.stop
          @mousedown.stop
          @click.stop
        >
          <!-- Header -->
          <div class="regen-header">
            <div class="regen-header-inner">
              <div class="regen-header-icon">
                <RefreshCw class="regen-header-icon-svg" />
              </div>
              <div class="regen-header-text">
                <p class="regen-title">Regenerate {{ sectionLabel }}</p>
                <p class="regen-subtitle">{{ isAutoDerived ? 'Auto-recalculate metrics, statuses, and insights from active sections' : "Upload a new document to refresh this section's data" }}</p>
              </div>
              <button
                type="button"
                class="regen-close"
                aria-label="Close"
                @click="isOpen = false"
              >
                <X class="regen-close-icon" />
              </button>
            </div>
          </div>

          <!-- Body -->
          <div class="regen-body">
            <!-- Info Banner -->
            <div class="regen-notice" :class="{ 'regen-notice--auto': isAutoDerived }">
              <Sparkles v-if="isAutoDerived" class="regen-notice-icon" style="color: #2563eb;" />
              <Info v-else class="regen-notice-icon" />
              <p v-if="isAutoDerived" class="regen-notice-text">
                <strong>Cross-Channel Auto-Derived:</strong> Executive Snapshot automatically derives KPIs, operational delivery statuses, and strategic insights directly from your active marketing sections (Social Media, Leads, SEO, Chatbot, Blogs, Paid Ads, AI Podcast).
                <br /><br />
                No file upload is required. Regenerating will recalculate headline metrics, refresh comparison chart data, update operational delivery checklists, and synthesize fresh insights from all available section data.
              </p>
              <p v-else class="regen-notice-text">
                <strong>Section Replacement:</strong> This will replace the raw data and metrics for
                <strong class="regen-notice-section">{{ sectionLabel }}</strong> only.
                All other sections and existing custom text remain unaffected.
              </p>
            </div>

            <!-- Auto-Derived Summary Card -->
            <div v-if="isAutoDerived" class="regen-auto-card">
              <div class="regen-auto-card-header">
                <RefreshCw class="regen-auto-card-icon" />
                <span>What will be recalculated:</span>
              </div>
              <ul class="regen-auto-list">
                <li><strong>Headline KPIs:</strong> Top metric per active channel (Social, Leads, SEO, Blogs, Ads, Podcast)</li>
                <li><strong>Comparative Bar Chart:</strong> Period-over-period multi-service comparison data</li>
                <li><strong>Current Status:</strong> Operational delivery checklist from active services</li>
                <li><strong>Insights &amp; Recommendations:</strong> Fresh strategic narrative synthesized from live channel data</li>
              </ul>
            </div>

            <!-- Upload Drop Zone (Clickable native label + Drag & Drop) -->
            <div v-if="!isAutoDerived" class="regen-upload-group">
              <label class="regen-label">
                Upload Document(s) <span class="regen-required">*</span>
              </label>
              <label
                class="regen-dropzone"
                :class="{ 'regen-dropzone--active': isDragging }"
                @dragover.prevent="isDragging = true"
                @dragleave.prevent="isDragging = false"
                @drop.prevent="handleDrop"
              >
                <input
                  ref="fileInputRef"
                  type="file"
                  class="regen-file-input"
                  multiple
                  accept=".pdf,.png,.jpg,.jpeg,.webp,.txt,.csv,.doc,.docx"
                  @change="handleFileChange"
                  @click.stop
                />
                <div class="regen-dropzone-icon-wrap">
                  <UploadCloud class="regen-dropzone-icon" />
                </div>
                <p class="regen-dropzone-heading">Click to select or drag &amp; drop documents</p>
                <p class="regen-dropzone-hint">PDF, PNG, JPG, CSV, or Text &mdash; Max 20MB per file</p>
              </label>
            </div>

            <!-- File List -->
            <div v-if="!isAutoDerived && selectedFiles.length > 0" class="regen-files">
              <div class="regen-files-header">
                <span class="regen-files-count">
                  {{ selectedFiles.length }} file{{ selectedFiles.length > 1 ? 's' : '' }} selected
                </span>
                <button type="button" class="regen-clear-btn" @click="selectedFiles = []">
                  Clear all
                </button>
              </div>
              <div class="regen-files-list">
                <div v-for="(file, index) in selectedFiles" :key="index" class="regen-file-item">
                  <div class="regen-file-info">
                    <FileText class="regen-file-icon" />
                    <span class="regen-file-name" :title="file.name">{{ file.name }}</span>
                    <span class="regen-file-size">{{ formatFileSize(file.size) }}</span>
                  </div>
                  <button
                    type="button"
                    class="regen-file-remove"
                    title="Remove file"
                    @click.stop="removeFile(index)"
                  >
                    <X class="regen-file-remove-icon" />
                  </button>
                </div>
              </div>
            </div>

            <!-- AI Model Selection -->
            <div class="regen-model-group">
              <label class="regen-label">{{ isAutoDerived ? 'AI Synthesis Model' : 'AI Extraction Model' }}</label>
              <div class="regen-model-select-wrap">
                <Sparkles class="regen-model-icon" />
                <select v-model="aiModel" class="regen-model-select">
                  <option value="gpt-4o-mini">OpenAI GPT-4o Mini &mdash; Default (Fast &amp; Accurate)</option>
                  <option value="gpt-4o">OpenAI GPT-4o &mdash; Deep Analysis (Slower)</option>
                  <option value="gpt-4.1">OpenAI GPT-4.1 &mdash; Latest High Quality</option>
                  <option value="gpt-4.1-mini">OpenAI GPT-4.1 Mini &mdash; Fast &amp; Capable</option>
                  <option value="gpt-4.1-nano">OpenAI GPT-4.1 Nano &mdash; Fastest &amp; Cheapest</option>
                  <option value="gpt-5">OpenAI GPT-5 &mdash; Flagship Reasoning</option>
                  <option value="gpt-5-mini">OpenAI GPT-5 Mini &mdash; Fast Reasoning</option>
                  <option value="gpt-5-nano">OpenAI GPT-5 Nano &mdash; Fastest</option>
                  <option value="gpt-4-turbo">OpenAI GPT-4 Turbo</option>
                  <option value="gpt-3.5-turbo">OpenAI GPT-3.5 Turbo (Legacy)</option>
                </select>
                <ChevronDown class="regen-model-chevron" />
              </div>
            </div>

            <!-- Error Banner -->
            <div v-if="errorMessage" class="regen-error">
              <AlertCircle class="regen-error-icon" />
              <span>{{ errorMessage }}</span>
            </div>
          </div>

          <!-- Footer -->
          <div class="regen-footer">
            <button
              type="button"
              class="regen-btn regen-btn--cancel"
              :disabled="isSubmitting"
              @click="isOpen = false"
            >
              Cancel
            </button>
            <button
              type="button"
              class="regen-btn regen-btn--primary"
              :disabled="(!isAutoDerived && selectedFiles.length === 0) || isSubmitting"
              @click="handleRegenerate"
            >
              <RefreshCw class="regen-btn-icon" :class="{ 'animate-spin': isSubmitting }" />
              {{ isSubmitting ? 'Regenerating…' : (isAutoDerived ? 'Regenerate Snapshot' : 'Regenerate Section') }}
            </button>
          </div>
        </div>
      </Transition>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import {
  RefreshCw,
  Info,
  UploadCloud,
  FileText,
  X,
  Sparkles,
  AlertCircle,
  ChevronDown,
} from "lucide-vue-next";
import { useToast } from "vue-toast-notification";
import { regenerateReportSection } from "@/services/reportsService";
import type { ReportRecord } from "@/types/reports";

const props = withDefaults(
  defineProps<{
    open: boolean;
    reportId: number | string | null;
    sectionKey: string;
    sectionLabel: string;
    isAutoDerived?: boolean;
    currentAiModel?: string;
  }>(),
  {
    isAutoDerived: false,
  }
);

const isAutoDerived = computed(() => {
  return (
    Boolean(props.isAutoDerived) ||
    ['executivesnapshot', 'executive', 'executive_snapshot'].includes(
      (props.sectionKey || '').toLowerCase().replace(/[-_]/g, '')
    )
  );
});

const emit = defineEmits<{
  (event: "update:open", value: boolean): void;
  (event: "regenerated", report: ReportRecord): void;
}>();

const toast = useToast();

const isOpen = computed({
  get: () => props.open,
  set: (val: boolean) => emit("update:open", val),
});

// Close on Escape key without affecting parent dialogs
function onKeydown(e: KeyboardEvent) {
  if (e.key === "Escape" && isOpen.value) {
    e.stopPropagation();
    e.stopImmediatePropagation();
    isOpen.value = false;
  }
}
onMounted(() => document.addEventListener("keydown", onKeydown, true));
onUnmounted(() => document.removeEventListener("keydown", onKeydown, true));

// Reset files and ensure pointer events are unlocked when dialog opens
watch(
  () => props.open,
  (val) => {
    if (val) {
      selectedFiles.value = [];
      errorMessage.value = null;
      if (typeof document !== "undefined") {
        document.body.style.pointerEvents = "auto";
      }
    }
  }
);

const fileInputRef = ref<HTMLInputElement | null>(null);
const selectedFiles = ref<File[]>([]);
const isDragging = ref(false);
const isSubmitting = ref(false);
const errorMessage = ref<string | null>(null);
const aiModel = ref<string>(props.currentAiModel || "gpt-4o-mini");

function handleFileChange(event: Event) {
  const target = event.target as HTMLInputElement;
  if (target.files && target.files.length > 0) {
    addFiles(Array.from(target.files));
    target.value = "";
  }
}

function handleDrop(event: DragEvent) {
  isDragging.value = false;
  if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
    addFiles(Array.from(event.dataTransfer.files));
  }
}

function addFiles(files: File[]) {
  errorMessage.value = null;
  selectedFiles.value = [...selectedFiles.value, ...files];
}

function removeFile(index: number) {
  selectedFiles.value.splice(index, 1);
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

async function handleRegenerate() {
  if (!props.reportId) {
    errorMessage.value = "No active report found to regenerate.";
    return;
  }
  if (!isAutoDerived.value && selectedFiles.value.length === 0) {
    errorMessage.value = "Please select at least one document to upload.";
    return;
  }

  isSubmitting.value = true;
  errorMessage.value = null;

  try {
    const updatedReport = await regenerateReportSection(
      props.reportId,
      props.sectionKey,
      selectedFiles.value,
      { aiModel: aiModel.value }
    );
    toast.success(`"${props.sectionLabel}" regenerated successfully!`);
    emit("regenerated", updatedReport);
    isOpen.value = false;
    selectedFiles.value = [];
  } catch (err: any) {
    console.error("Failed regenerating report section:", err);
    errorMessage.value =
      err?.response?.data?.message ||
      err?.message ||
      "An unexpected error occurred while regenerating this section.";
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<style scoped>
/* ── Modal Root (Top-level container over entire viewport) ── */
.regen-modal-root {
  position: fixed;
  inset: 0;
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto !important;
  font-family: 'Poppins', sans-serif;
}

/* ── Backdrop ─────────────────────────────────────────────── */
.regen-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.65);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  z-index: 1;
  pointer-events: auto !important;
  cursor: pointer;
}

/* ── Shell (the modal box) ────────────────────────────────── */
.regen-shell {
  position: relative;
  z-index: 2;
  width: min(530px, calc(100vw - 2rem));
  max-height: 88vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 1.35rem;
  border: 1px solid rgba(218, 226, 238, 0.95);
  box-shadow: 0 24px 64px rgba(15, 23, 42, 0.32);
  pointer-events: auto !important;
}

/* ── Interactive Elements must receive pointer events ─────── */
.regen-close,
.regen-dropzone,
.regen-file-input,
.regen-clear-btn,
.regen-file-remove,
.regen-model-select,
.regen-btn {
  pointer-events: auto !important;
}

/* ── Transitions ─────────────────────────────────────────── */
.regen-fade-enter-active,
.regen-fade-leave-active {
  transition: opacity 0.2s ease;
}
.regen-fade-enter-from,
.regen-fade-leave-to {
  opacity: 0;
}

.regen-pop-enter-active {
  transition: opacity 0.2s ease, transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.regen-pop-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}
.regen-pop-enter-from {
  opacity: 0;
  transform: translateY(12px) scale(0.97);
}
.regen-pop-leave-to {
  opacity: 0;
  transform: translateY(6px) scale(0.98);
}

/* ── Header ───────────────────────────────────────────────── */
.regen-header {
  border-bottom: 1px solid #e8eef8;
  padding: 1.15rem 1.5rem;
  flex-shrink: 0;
  background: #ffffff;
}
.regen-header-inner {
  display: flex;
  align-items: center;
  gap: 0.875rem;
}
.regen-header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.4rem;
  height: 2.4rem;
  min-width: 2.4rem;
  border-radius: 0.7rem;
  background: #eff6ff;
  color: #2563eb;
}
.regen-header-icon-svg {
  width: 1.05rem;
  height: 1.05rem;
}
.regen-header-text {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
  flex: 1;
}
.regen-title {
  font-size: 1rem;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin: 0;
}
.regen-subtitle {
  font-size: 0.7rem;
  color: #64748b;
  font-weight: 500;
  margin: 0;
}
.regen-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.8rem;
  height: 1.8rem;
  min-width: 1.8rem;
  border-radius: 0.5rem;
  border: none;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.regen-close:hover {
  background: #f1f5f9;
  color: #475569;
}
.regen-close-icon {
  width: 1rem;
  height: 1rem;
}

/* ── Body ─────────────────────────────────────────────────── */
.regen-body {
  padding: 1.15rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

/* ── Notice ───────────────────────────────────────────────── */
.regen-notice {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  border-radius: 0.85rem;
  border: 1px solid #bfdbfe;
  background: #f0f7ff;
  padding: 0.7rem 0.9rem;
}
.regen-notice-icon {
  width: 0.95rem;
  height: 0.95rem;
  color: #2563eb;
  flex-shrink: 0;
  margin-top: 0.1rem;
}
.regen-notice-text {
  font-size: 0.7rem;
  color: #1e40af;
  line-height: 1.55;
  margin: 0;
}
.regen-notice-section {
  color: #1d4ed8;
}
.regen-notice--auto {
  background: #f8faff;
  border-color: #cbd8ee;
}
.regen-auto-card {
  border-radius: 0.85rem;
  border: 1px solid #dbeafe;
  background: linear-gradient(135deg, #f8fafc 0%, #edf4ff 100%);
  padding: 0.85rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}
.regen-auto-card-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.76rem;
  font-weight: 700;
  color: #1e40af;
}
.regen-auto-card-icon {
  width: 0.9rem;
  height: 0.9rem;
  color: #2563eb;
}
.regen-auto-list {
  margin: 0;
  padding-left: 1.15rem;
  font-size: 0.7rem;
  line-height: 1.55;
  color: #334155;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

/* ── Label ────────────────────────────────────────────────── */
.regen-label {
  display: block;
  font-size: 0.65rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #748197;
  margin-bottom: 0.4rem;
}
.regen-required {
  color: #dc2626;
}

/* ── Drop zone ────────────────────────────────────────────── */
.regen-upload-group {
  display: flex;
  flex-direction: column;
}
.regen-dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3rem;
  border-radius: 1rem;
  border: 2px dashed #c8d8ee;
  background: #f8fafc;
  padding: 1.4rem 1rem;
  text-align: center;
  cursor: pointer;
  user-select: none;
  transition: border-color 0.18s, background 0.18s;
}
.regen-dropzone:hover,
.regen-dropzone--active {
  border-color: #3b82f6;
  background: rgba(239, 246, 255, 0.55);
}
.regen-file-input {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}
.regen-dropzone-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.6rem;
  height: 2.6rem;
  border-radius: 50%;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.06);
  color: #3b82f6;
  margin-bottom: 0.2rem;
  transition: transform 0.18s;
}
.regen-dropzone:hover .regen-dropzone-icon-wrap {
  transform: scale(1.06);
}
.regen-dropzone-icon {
  width: 1.2rem;
  height: 1.2rem;
}
.regen-dropzone-heading {
  font-size: 0.76rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}
.regen-dropzone-hint {
  font-size: 0.66rem;
  color: #64748b;
  margin: 0;
}

/* ── File list ────────────────────────────────────────────── */
.regen-files {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}
.regen-files-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.regen-files-count {
  font-size: 0.7rem;
  font-weight: 700;
  color: #475569;
}
.regen-clear-btn {
  font-size: 0.68rem;
  font-weight: 700;
  color: #dc2626;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
}
.regen-clear-btn:hover {
  text-decoration: underline;
  opacity: 0.8;
}
.regen-files-list {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  max-height: 8rem;
  overflow-y: auto;
  padding-right: 2px;
}
.regen-file-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  border-radius: 0.75rem;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  padding: 0.45rem 0.7rem;
}
.regen-file-info {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  min-width: 0;
  flex: 1;
}
.regen-file-icon {
  width: 0.8rem;
  height: 0.8rem;
  color: #64748b;
  flex-shrink: 0;
}
.regen-file-name {
  font-size: 0.7rem;
  font-weight: 600;
  color: #1e293b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
}
.regen-file-size {
  font-size: 0.63rem;
  color: #94a3b8;
  font-weight: 500;
  flex-shrink: 0;
}
.regen-file-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.4rem;
  height: 1.4rem;
  min-width: 1.4rem;
  border-radius: 0.4rem;
  border: none;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.regen-file-remove:hover {
  background: #fee2e2;
  color: #dc2626;
}
.regen-file-remove-icon {
  width: 0.75rem;
  height: 0.75rem;
}

/* ── AI Model ─────────────────────────────────────────────── */
.regen-model-group {
  display: flex;
  flex-direction: column;
}
.regen-model-select-wrap {
  position: relative;
  display: flex;
  align-items: center;
}
.regen-model-icon {
  position: absolute;
  left: 0.75rem;
  width: 0.85rem;
  height: 0.85rem;
  color: #3b82f6;
  pointer-events: none;
}
.regen-model-chevron {
  position: absolute;
  right: 0.75rem;
  width: 0.85rem;
  height: 0.85rem;
  color: #94a3b8;
  pointer-events: none;
}
.regen-model-select {
  width: 100%;
  border-radius: 0.85rem;
  border: 1px solid #d8e4f0;
  background: #ffffff;
  padding: 0.6rem 2.2rem;
  font-family: inherit;
  font-size: 0.76rem;
  font-weight: 600;
  color: #173b5f;
  cursor: pointer;
  appearance: none;
  transition: border-color 0.18s, box-shadow 0.18s;
}
.regen-model-select:focus {
  border-color: #2563eb;
  outline: none;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}

/* ── Error ────────────────────────────────────────────────── */
.regen-error {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  border-radius: 0.85rem;
  border: 1px solid #fecaca;
  background: #fef2f2;
  padding: 0.6rem 0.85rem;
  font-size: 0.7rem;
  color: #b91c1c;
  line-height: 1.5;
}
.regen-error-icon {
  width: 0.85rem;
  height: 0.85rem;
  flex-shrink: 0;
  margin-top: 0.1rem;
  color: #dc2626;
}

/* ── Footer ───────────────────────────────────────────────── */
.regen-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.6rem;
  border-top: 1px solid #e8eef8;
  background: #fcfdff;
  padding: 0.85rem 1.5rem;
  flex-shrink: 0;
  border-radius: 0 0 1.35rem 1.35rem;
}
.regen-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  border-radius: 0.75rem;
  font-family: inherit;
  font-size: 0.76rem;
  font-weight: 700;
  padding: 0.5rem 1.15rem;
  cursor: pointer;
  border: none;
  transition: opacity 0.18s, background 0.18s, box-shadow 0.18s;
  white-space: nowrap;
}
.regen-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.regen-btn--cancel {
  background: #ffffff;
  color: #475569;
  border: 1px solid #cbd5e1;
}
.regen-btn--cancel:not(:disabled):hover {
  background: #f1f5f9;
  color: #1e293b;
}
.regen-btn--primary {
  background: linear-gradient(135deg, #0f3557 0%, #2563eb 100%);
  color: #ffffff;
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.3);
}
.regen-btn--primary:not(:disabled):hover {
  opacity: 0.92;
  box-shadow: 0 4px 14px rgba(37, 99, 235, 0.4);
}
.regen-btn-icon {
  width: 0.82rem;
  height: 0.82rem;
}
</style>